import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Globe,
  FileText,
  BarChart3,
  Database,
  ChevronRight,
  Settings,
  Lock,
  AlertTriangle,
} from 'lucide-react'
import { useEffect } from 'react'
import { useStorageStore } from '../store/useStorageStore'
import StorageStatusBar from './StorageStatusBar'
import UserSceneNotification from './UserSceneNotification'
import { workspaceMappings } from '../config/workspaceMappings'
import useUserSceneStore from '../store/useUserSceneStore'
import {
  getBootstrapPublishReadiness,
  isBootstrapTokenProfile,
  readBootstrapConfig,
} from '../utils/bootstrapConfig'

const menuItems = [
  {
    title: '控制面板',
    icon: LayoutDashboard,
    path: '/',
  },
  {
    title: '镜像配置',
    icon: Globe,
    path: '/mirror',
    children: workspaceMappings['/mirror'].secondaryButtons.map((button) => ({
      title: button.label,
      path: button.path,
    })),
  },
  {
    title: '内容工厂',
    icon: FileText,
    path: '/content',
    children: workspaceMappings['/content'].secondaryButtons.map((button) => ({
      title: button.label,
      path: button.path,
    })),
  },
  {
    title: '流量分析',
    icon: BarChart3,
    path: '/analytics',
    children: workspaceMappings['/analytics'].secondaryButtons.map((button) => ({
      title: button.label,
      path: button.path,
    })),
  },
  {
    title: '存储管理',
    icon: Database,
    path: '/storage',
    children: workspaceMappings['/storage'].secondaryButtons.map((button) => ({
      title: button.label,
      path: button.path,
    })),
  },
]

function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isKVWarning } = useStorageStore()
  const { forceLocked, lockedTarget, isDetecting, originServer, detectUserScene } = useUserSceneStore()

  useEffect(() => {
    detectUserScene()
  }, [detectUserScene])

  useEffect(() => {
    const config = readBootstrapConfig()
    const shouldForceDomainCheck = !config?.domainCheckCompleted && location.pathname !== '/mirror/source'

    if (shouldForceDomainCheck) {
      navigate('/mirror/source', { replace: true })
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    const config = readBootstrapConfig()
    if (!config?.domainCheckCompleted) return

    const readiness = getBootstrapPublishReadiness(config)
    if (location.pathname.startsWith('/content') && !readiness.ready) {
      navigate(readiness.firstUnmetPath, {
        replace: true,
        state: { blockedBy: 'publish_readiness' },
      })
    }
  }, [location.pathname, navigate])

  useEffect(() => {
    if (forceLocked && lockedTarget && location.pathname !== lockedTarget) {
      navigate(lockedTarget, { replace: true })
    }
  }, [forceLocked, lockedTarget, location.pathname, navigate])

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const bootstrapConfig = readBootstrapConfig()
  const publishReadiness = getBootstrapPublishReadiness(bootstrapConfig)
  const firstPublishUnmet = publishReadiness.firstUnmet
  const isPublishFlowLocked = !publishReadiness.ready

  const isLockedPath = (path) => {
    if (forceLocked) {
      return path !== '/mirror' && path !== '/mirror/factory'
    }
    if (isPublishFlowLocked && path === '/content') {
      return true
    }
    return false
  }

  const isLockedSubmenu = (path) => {
    if (forceLocked) {
      return path !== '/mirror/factory'
    }
    if (isPublishFlowLocked && path.startsWith('/content/')) {
      return true
    }
    return false
  }

  const showBootstrapTokenReminder =
    Boolean(bootstrapConfig?.onboardingCompleted) && isBootstrapTokenProfile(bootstrapConfig)
  const downgradeRecommendation =
    bootstrapConfig?.downgradeRecommendation && typeof bootstrapConfig.downgradeRecommendation === 'object'
      ? bootstrapConfig.downgradeRecommendation
      : null
  const recommendedDeadlineHours = Number(downgradeRecommendation?.deadlineHours || 0)
  const tokenReminderMessage =
    String(downgradeRecommendation?.message || '').trim() ||
    'Switch to a runtime token profile after onboarding to reduce long-term risk.'

  return (
    <div className="flex h-screen bg-gray-50">
      <aside className="w-sidebar bg-navy-900 flex flex-col flex-shrink-0">
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <span className="text-white font-semibold text-lg">Mirror-X</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {menuItems.map((item) => {
            const locked = isLockedPath(item.path)
            return (
              <div key={item.path} className="mb-1">
                {locked ? (
                  <div
                    className={`sidebar-item opacity-50 cursor-not-allowed ${
                      isActive(item.path) ? 'active' : ''
                    }`}
                    onClick={(e) => e.preventDefault()}
                  >
                    <item.icon size={20} />
                    <span className="flex-1">{item.title}</span>
                    <Lock size={14} className="text-gray-500" />
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive: active }) =>
                      `sidebar-item ${active || isActive(item.path) ? 'active' : ''}`
                    }
                  >
                    <item.icon size={20} />
                    <span className="flex-1">{item.title}</span>
                    {item.children && (
                      <ChevronRight
                        size={16}
                        className={`transition-transform ${isActive(item.path) ? 'rotate-90' : ''}`}
                      />
                    )}
                    {item.path === '/storage' && isKVWarning && (
                      <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                    )}
                  </NavLink>
                )}

                {item.children && isActive(item.path) && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.children.map((child) => {
                      const childLocked = isLockedSubmenu(child.path)
                      return childLocked ? (
                        <div
                          key={child.path}
                          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-gray-500 cursor-not-allowed"
                        >
                          <span className="flex-1">{child.title}</span>
                          <Lock size={12} />
                        </div>
                      ) : (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                              isActive
                                ? 'text-white bg-primary-600/50'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`
                          }
                        >
                          {child.title}
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <StorageStatusBar />
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <h1 className="text-xl font-semibold text-gray-900">
            {menuItems.find((item) => isActive(item.path))?.title || '控制面板'}
          </h1>
          <div className="flex items-center gap-4">
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Settings size={20} />
            </button>
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-700 font-medium text-sm">A</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          {showBootstrapTokenReminder && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-blue-800 font-semibold mb-1">Token security reminder</h3>
              <p className="text-blue-700 text-sm">{tokenReminderMessage}</p>
              {recommendedDeadlineHours > 0 && (
                <p className="text-blue-700 text-xs mt-1">
                  Recommended rotation window: {recommendedDeadlineHours} hours.
                </p>
              )}
            </div>
          )}

          {isPublishFlowLocked && firstPublishUnmet && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <h3 className="text-amber-800 font-semibold mb-1">发布中心已临时锁定</h3>
              <p className="text-amber-700 text-sm">
                需要先完成「{firstPublishUnmet.label}」后，才能进入内容发布。
              </p>
              <button
                onClick={() => navigate(firstPublishUnmet.route)}
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors"
              >
                {firstPublishUnmet.action || '去完成前置步骤'}
              </button>
            </div>
          )}

          {forceLocked && !isDetecting && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="text-amber-800 font-semibold mb-1">需要先部署模板</h3>
                <p className="text-amber-700 text-sm">
                  {originServer.is404
                    ? '原站返回 404 错误，请先在模板工厂部署一个模板站点。'
                    : `原站内容不足（${originServer.contentLength || 0} 字节），请先在模板工厂部署模板。`}
                </p>
              </div>
            </div>
          )}
          <UserSceneNotification />
          <Outlet />
        </div>
      </main>
    </div>
  )
}

export default MainLayout
